/**
 * The Kinde provider, selected when `GET /api/v1/auth/config` answers `provider: "kinde"`.
 *
 * Configured from that response at runtime and never from the build. There is no `VITE_KINDE_*`
 * variable in this codebase and there must not be one: inlining identity configuration at build
 * time makes switching provider a frontend rebuild, which is a much worse place to discover the
 * constraint than a deployment.
 *
 * Two components rather than one function, because the SDK publishes a hook and a hook cannot be
 * called from the `useMemo` the other providers are built in. `KindeGate` mounts the SDK;
 * `KindeBridge` sits inside it, reads the hook, and supplies the same `AuthContextValue` shape
 * `local` and `jwt` supply — so nothing above the seam knows which provider it got.
 *
 * **No role is read from Kinde.** The token says who somebody is; what they may do, which matter
 * and which registers all come from `GET /meta`, which the server answers from `app_user` and its
 * grants. Reading a permission claim here would put a firm's decision about who may sign off
 * legal advice in a dashboard owned by somebody else.
 */

import { useEffect, type ReactNode } from 'react';
import { KindeProvider, useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { setAccessTokenSource } from '~/api/client';
import { Button } from '~/ui/Button';
import styles from './SignIn.module.css';
import type * as T from '~/api/schema';
import type { AuthContextValue } from './types';

/** Where Kinde returns the browser. Registered on the application in Kinde, and must match it. */
export const CALLBACK_PATH = '/auth/callback';

export function KindeGate({
  config,
  render,
}: {
  config: T.AuthConfig['kinde'];
  render: (value: AuthContextValue) => ReactNode;
}) {
  if (!config?.domain || !config.clientId) {
    // The server said kinde and then did not say which tenant. Failing loudly beats a sign-in
    // button that redirects nowhere.
    throw new Error('auth/config named kinde without a domain and client id');
  }

  return (
    <KindeProvider
      domain={config.domain}
      clientId={config.clientId}
      redirectUri={config.redirectUri ?? `${window.location.origin}${CALLBACK_PATH}`}
      logoutUri={config.logoutUri ?? window.location.origin}
      {...(config.audience ? { audience: config.audience } : {})}
    >
      <KindeBridge render={render} />
    </KindeProvider>
  );
}

function KindeBridge({ render }: { render: (value: AuthContextValue) => ReactNode }) {
  const kinde = useKindeAuth();

  const value: AuthContextValue = {
    status: kinde.isLoading ? 'loading' : kinde.isAuthenticated ? 'authenticated' : 'unauthenticated',
    user: kinde.user
      ? {
          id: String(kinde.user.id ?? ''),
          displayName:
            [kinde.user.givenName, kinde.user.familyName].filter(Boolean).join(' ') ||
            (kinde.user.email ?? 'Signed in'),
          email: kinde.user.email ?? null,
          // Empty, deliberately. `/meta` is the authority on what this person may do, and a role
          // guessed from a token would disagree with it the moment an administrator changes one.
          roles: [],
        }
      : null,
    getAccessToken: async () => {
      try {
        return (await kinde.getAccessToken()) ?? null;
      } catch {
        // An expired session is "not signed in", not a crash: the shell shows the button again.
        return null;
      }
    },
    login: (opts) => {
      // The return path is remembered here rather than handed to the SDK. Its option for this is
      // not in the published types, and a deep link surviving sign-in is worth more than the
      // tidiness of letting the library carry it: somebody opening a shared link to one
      // register's rules should land on that register, not on the home page.
      rememberReturn(opts?.returnTo ?? `${window.location.pathname}${window.location.search}`);
      void kinde.login();
    },
    logout: () => {
      void kinde.logout();
    },
  };

  // Installed during render for the same reason the other providers are: an effect runs after the
  // children have mounted and their queries have already fired, which leaves the first read of
  // every page load unauthenticated.
  if (value.status !== 'loading') {
    setAccessTokenSource(value.getAccessToken);
  }

  return <>{render(value)}</>;
}

/**
 * The callback route. Kinde's SDK completes the code exchange as it mounts, so this only has to
 * exist and say something while that happens.
 */
export function KindeCallback() {
  const kinde = useKindeAuth();

  useEffect(() => {
    if (kinde.isLoading || !kinde.isAuthenticated) return;
    // `replace`, not `push`: the callback address carries a spent authorisation code, and the
    // back button must not return to it.
    window.location.replace(takeReturn());
  }, [kinde.isLoading, kinde.isAuthenticated]);

  return <p style={{ padding: 24 }}>Signing you in…</p>;
}

const RETURN_KEY = 'thelaw.returnTo';

function rememberReturn(path: string): void {
  try {
    // Only a path from this origin. Remembering whatever was handed in would make the callback
    // an open redirect, which is a way of phishing that looks exactly like signing in.
    if (path.startsWith('/') && !path.startsWith('//')) {
      sessionStorage.setItem(RETURN_KEY, path);
    }
  } catch {
    // Private windows and blocked storage both land here. The home page is a fine fallback.
  }
}

function takeReturn(): string {
  try {
    const held = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    if (held && held.startsWith('/') && !held.startsWith('//')) return held;
  } catch {
    // As above.
  }
  return '/';
}

/**
 * What somebody sees before they have signed in, under Kinde.
 *
 * A button rather than a form: the credentials are Kinde's business and this application never
 * sees them, which is most of the reason for using an identity provider at all. It borrows
 * `SignIn`'s stylesheet so the two look like one product.
 */
export function KindeSignIn({ onSignIn }: { onSignIn: () => void }) {
  return (
    <main className={styles.screen}>
      <div className={styles.card}>
        <h1 className={styles.title}>The Law</h1>
        <p className={styles.blurb}>
          Sign in to review findings. Your decisions are recorded against your name, so the next
          review can read them.
        </p>
        <Button variant="primary" onClick={onSignIn}>
          Sign in
        </Button>
      </div>
    </main>
  );
}
